import React, { Suspense, useEffect, useRef, useState } from 'react'
import Header from '../../components/atoms/Header'
import Text from '../../components/atoms/Text'
import Grade from '../../components/molecules/Result/Grade'
import MealDetails from '../../components/molecules/Result/MealDetails'
import FoodBreakdown from '../../components/molecules/Result/FoodBreakdown'
import Button from '../../components/atoms/GoodFlipButton'
import UnlockKidneyScore from '../../components/molecules/Result/UnlockKidneyScore'
import RequestCallback from '../../components/molecules/Result/RequestCallback'
import PageEndFlower from '../../assets/images/page-end-flower.webp'
import ArrowIcon from '../../components/atoms/Arrow'
import { useDispatch, useSelector } from 'react-redux'
import {
  generatePndsScoreRequest,
  resetPndsScore,
  setSuccessFlag,
} from '../../redux/slices'
import { useNavigate } from 'react-router-dom'
import FoodBreakDown from '../FoodBreakdown'
import AllFoodItemListing from '../../components/organisms/AllFoodItemListing'
import MobileLayout from '../../components/MobileLayout'
import CallbackSuccessPage from '../../components/molecules/Result/CallbackSuccessPage'
import PotassiumSummaryCard from '../../components/molecules/Result/PotassiumSummaryCard'
import {
  createPNDSPayload,
  sendMessageToParent,
} from '../../utils/helpers/common'
import Loader from '../../components/atoms/Loader'
import { EVENTS } from '../../analytics/events'
import { trackEventCKD } from '../../analytics'

const ResultPage = () => {
  const foodBreakdownRef = useRef(null)
  const allFoodListRef = useRef(null)
  const { pndsScore, capturedImage } = useSelector((s) => s.foodScan)
  const { questions } = useSelector((state) => state.questionnaire)
  const { userDetails } = useSelector((state) => state.auth)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedFood, setSelectedFood] = useState(null)
  const [showCallbackSuccess, setShowCallbackSuccess] = useState(false)

  const onPressScanAgain = () => {
    dispatch(resetPndsScore())
    navigate('/scan-food/capture?from=result')
  }

  const onPressBack = () => {
    sendMessageToParent('NAVIGATE_BACK')
    navigate('/')
  }

  const onClickFoodBreakdownItem = (foodItem) => {
    allFoodListRef.current?.hide()
    setSelectedFood(foodItem)
    foodBreakdownRef.current?.show()
  }

  const onClickViewMore = () => {
    foodBreakdownRef.current?.hide()
    allFoodListRef.current?.show()
  }

  const onPressUnlockKidneyScore = () => {
    dispatch(setSuccessFlag(false))
    navigate('/ckd/questionnaire?from=SCORED&fromButtonPress=true')
  }

  const onCallbackSuccess = () => {
    setShowCallbackSuccess(true)
  }

  const getPndsAnalysis = () => {
    const payload = createPNDSPayload(questions, capturedImage)
    dispatch(generatePndsScoreRequest(payload))
  }

  const onScoreGenerationFailed = () => navigate(-1)

  useEffect(() => {
    if (!pndsScore.loading && !!pndsScore.error) onScoreGenerationFailed()
  }, [pndsScore])

  useEffect(() => {
    if (!!pndsScore.success) {
      console.log('trackEventCKD --> CKD_LKF_Score_FoodGrade')
      const extraEventData = {
        country_code: userDetails?.data?.country_code || '+91',
        contact_no: userDetails?.data?.contact_no || '',
        food_grade: pndsScore?.data?.meal_details?.grade,
      }
      trackEventCKD(EVENTS.CKD_LKF_Score_FoodGrade, extraEventData)
    }
  }, [pndsScore.success])

  useEffect(() => {
    getPndsAnalysis()
  }, [])

  return (
    <MobileLayout>
      {pndsScore?.loading ? (
        <Suspense fallback={<div>Loading questions...</div>}>
          <Loader
            title={
              'Loading... Why did the doctor carry a red pen? In case they needed to draw blood.'
            }
          />
        </Suspense>
      ) : showCallbackSuccess ? (
        <CallbackSuccessPage onClick={() => setShowCallbackSuccess(false)} />
      ) : (
        <div className="relative z-[10] bg-white min-h-screen">
          <Header title="Back" onPressLeftIcon={onPressBack} />

          {/* Hero: Title + Grade + Meal */}
          <div className="lg:max-w-md lg:mx-auto">
            <Text
              tag={'p'}
              variant={'title'}
              size={'small'}
              className={'text-text-primary mt-6 mb-2'}
              textalign={'center'}
            >
              Your Food Analysis
            </Text>
            <Grade grade={pndsScore?.data?.meal_details?.grade} />
            <MealDetails
              grade={pndsScore?.data?.meal_details?.grade}
              onClickViewMore={onClickViewMore}
            />
          </div>

          {/* {pndsScore?.data?.potassium_summary && (
            <PotassiumSummaryCard
              grade={pndsScore?.data?.meal_details?.grade}
              title="Potassium Friendliness"
              summary={pndsScore?.data?.potassium_summary}
              className="lg:max-w-4xl lg:mx-auto mx-4 mt-6"
            />
          )} */}

          <div className="h-4" />

          {/* Breakdown + Scan Again */}
          <div className="lg:max-w-4xl lg:mx-auto">
            <FoodBreakdown
              onClickItem={onClickFoodBreakdownItem}
              onClickViewMore={onClickViewMore}
              pndsScore={pndsScore}
            />
            <div className="my-16 mx-4 border border-border-brand-primary bg-background-brand-secondary rounded-4 px-3 pt-4 pb-5 flex flex-row items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <Text
                  tag={'p'}
                  variant={'body'}
                  size={'medium'}
                  className={'text-text-primary'}
                  bold={'true'}
                >
                  Scan more meals
                </Text>
                <Text
                  tag={'p'}
                  variant={'body'}
                  size={'small'}
                  className={'text-text-quartenary'}
                >
                  See if your meals are good for your kidney
                </Text>
              </div>
              <Button
                className="!mx-0 min-w-[180px]"
                title={'Scan Again'}
                type={'goodflip_primary'}
                onClick={onPressScanAgain}
                leftIcon={null}
                rightIcon={null}
              />
            </div>
          </div>

          {/* Unlock Kidney Score */}
          <div className="lg:max-w-4xl lg:mx-auto">
            <UnlockKidneyScore onClick={onPressUnlockKidneyScore} />
          </div>

          <div className="h-16" />

          {/* Request Callback */}
          <div className="max-w-full">
            <RequestCallback onSuccess={onCallbackSuccess} />
          </div>

          <div className="h-16" />
          <div className="max-w-xs mx-auto">
            <img
              src={PageEndFlower}
              alt={'flower-balls'}
              className="w-full aspect-square"
            />
          </div>
          <div className="h-20" />

          {/* Fixed footer */}
          <div className="fixed max-w-lg lg:max-w-5xl mx-auto bottom-0 left-0 right-0 bg-[#FFFFFFBF] backdrop-blur-2xl w-full py-4 z-20">
            <Button
              title={'Unlock Your Kidney Score'}
              type={'goodflip_primary'}
              disabled={false}
              leftIcon={null}
              rightIcon={<ArrowIcon direction="right" color="#FFFFFF" />}
              onClick={onPressUnlockKidneyScore}
            />
          </div>
        </div>
      )}

      <FoodBreakDown
        ref={foodBreakdownRef}
        selectedFood={selectedFood}
        onClickViewMore={onClickViewMore}
      />
      <AllFoodItemListing
        ref={allFoodListRef}
        foodItems={pndsScore?.data?.food_details}
        onClickItem={onClickFoodBreakdownItem}
      />
    </MobileLayout>
  )
}

export default ResultPage
